import { useDispatch, useSelector } from 'react-redux';
import { FormGuardOperations, FormGuardSelectors } from '../ducks/formGuard';

/**
 * Custom hook to manage the "dirty" state of a form.
 *
 * This hook provides methods to set the form's dirty state and to reset it to clean.
 * It interacts with the Redux store to update the form's dirty state.
 *
 * @returns {Object} An object containing the following methods:
 *   - `setFormIsDirty(isDirty: boolean): void` - Updates the form's dirty state.
 *     If the new state differs from the current state, it dispatches an action to update the Redux store.
 *   - `cleanFormIsDirty(): void` - Resets the form's dirty state to `false`.
 */
function useFormIsDirty() {
  const dispatch = useDispatch();
  const { dirtyForm } = useSelector(FormGuardSelectors.getFormGuardState);

  const setFormIsDirty = isDirty => {
    if (isDirty !== dirtyForm) {
      dispatch(FormGuardOperations.setDirtyForm(isDirty));
    }
  };

  return {
    setFormIsDirty,
    cleanFormIsDirty: () => setFormIsDirty(false)
  };
}

export default useFormIsDirty;
